import { Box, CircularProgress, Grid, IconButton, Tooltip, Typography, useTheme } from "@mui/material";
import { useGetScenesQuery } from "../../services/scene.service";
import { Params, useParams } from "react-router-dom";
import { useAppSelector } from "../../store/hooks";
import { IHotspot } from "@virtus-tech-repository/virtus-tech-repository/lib/models/hotspot.model";
import { EDraggableOptions } from "@virtus-tech-repository/virtus-tech-repository/lib/models/media.model";
import { useDispatch } from "react-redux";
import { useEffect, useRef, useState } from "react";
import { useGetScenarioOverviewQuery } from "../../services/scenario.service";
import { useGetMarksQuery, useUpdateMarksMutation } from "../../services/marks.service";
import {
    VTButton,
    VTMarksDataEntryTable,
    VTMarksTableAccordianWrapper,
    VTZoomInOut,
} from "@virtus-tech-repository/virtus-tech-repository";
import { VTMarksDataEntryRowObject } from "@virtus-tech-repository/virtus-tech-repository/lib/components/VTMarksDataEntryTable";
import {
    MarksDataStructure,
    MarkedHotspot,
    QuestionHotspot,
    ScenesMarks,
} from "@virtus-tech-repository/virtus-tech-repository/lib/models/marks.model";
import { IScene } from "@virtus-tech-repository/virtus-tech-repository/lib/models/scenario.model";
import DoneOutlineRoundedIcon from "@mui/icons-material/DoneOutlineRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import ZoomInRoundedIcon from "@mui/icons-material/ZoomInRounded";
import ZoomOutRoundedIcon from "@mui/icons-material/ZoomOutRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import _ from "lodash";

// might need this function going forward, if stays redundant, delete it
const isObjectEmpty = (objectName: {}) => {
    return Object.keys(objectName).length === 0;
};

const createDeepCopy = (obj: {}) => {
    return JSON.parse(JSON.stringify(obj));
};

// TODO: robin, tune this logic to be even better for deterimining default hotspot answer weightings
function getDefaultWeightingScore(hotspot: IHotspot): number {
    if (hotspot.actions && hotspot?.type === EDraggableOptions.ANSWER) {
        if (hotspot?.actions.appFeedback?.proceed) {
            return +1;
        } else if (hotspot.actions.movement?.sceneId) {
            return 0;
        } else {
            return -1;
        }
    }

    return 0;
}

// TODO: robin, tune this logic to be even better for deterimining default hotspot names (non-answer hotspots)
function hotspotTypeEnumToString(hotspotType: EDraggableOptions) {
    if (hotspotType === EDraggableOptions.INFORMATION) {
        return "Information Hotspot";
    }

    if (hotspotType === EDraggableOptions.MOVEMENT) {
        return "Movement Hotspot";
    }

    if (hotspotType === EDraggableOptions.QUESTION) {
        return "Question";
    }

    return "not an answer hotspot";
}

interface HotspotMarks {
    // hotspotId: string;
    hotspotName: string;
    marked: boolean;
    correct: boolean;
    weighting: number;
}

type QuestionAnswerHotspots = { timeInSeconds: number; hotspotMarks: HotspotMarks[] };
export type MarksHotspotData = {
    [key: string]: {
        [key: string]: HotspotMarks[] | QuestionAnswerHotspots;
    };
};

interface IEditMarksContainerProps {
    drawWidth: number;
}

export default function EditMarksContainer({ drawWidth }: IEditMarksContainerProps) {
    const theme = useTheme();
    const dispatch = useDispatch();
    const { scenarioId }: Readonly<Params<string>> = useParams();

    const { currentScenesTimelineHeight } = useAppSelector((state) => state.currentReducer);

    const { data: scenarioOverviewData } = useGetScenarioOverviewQuery(scenarioId ? scenarioId : "");
    const {
        data: marksData,
        currentData: marksCurrentData,
        isLoading: isLoadingMarks,
        error: marksError,
        isFetching: isFetchingMarks,
        refetch: refetchMarks,
    } = useGetMarksQuery(scenarioId!, {
        // pollingInterval: 5000, // get marks from server every 5secs
    });

    const [
        updateMarks,
        {
            isLoading: isUpdatingMarks,
            isSuccess: postingMarksIsSuccess,
            isError: postingMarksIsError,
            isUninitialized: postingMarksIsUninitialized,
        },
    ] = useUpdateMarksMutation();

    const [tableZoom, setTableZoom] = useState<number>(0.7);

    function updateMarksDebounce(marksData: MarksDataStructure, debounceTimeInMilliseconds: number) {
        updateMarks({
            scenarioId,
            data: marksData,
        });
        _.debounce(() => {
            // update redux toolkit query cache for marks service
        }, debounceTimeInMilliseconds);
    }

    const [marshaledScenesDataAsMarks, setMarshaledScenesDataAsMarks] = useState<MarksDataStructure>();

    const [loading, setLoading] = useState<boolean>(true);
    useEffect(() => {
        window.setTimeout(() => {
            setLoading(false);
        }, 2000);
    }, []);

    useEffect(() => {
        console.log("hook triggered", scenarioOverviewData);
        if (scenarioOverviewData) {
            console.log("block executing");
            const marshaledScenesDataAsMarks_ = scenarioOverviewData?.scenes
                .filter((scene: IScene) => scene.hotspots.length > 0)
                .sort((sceneA: IScene, sceneB: IScene) => sceneA.order! - sceneB.order!)
                .map((scene: IScene) => {
                    return {
                        sceneId: scene.id,
                        sceneName: scene.name,
                        sceneHotspots: scene.hotspots.map((hotspot) => {
                            if (hotspot.type === EDraggableOptions.QUESTION) {
                                const questionHotspotObj: QuestionHotspot = {
                                    hotspotId: hotspot.id,
                                    hotspotType: hotspot.type,
                                    hotspotTitle: hotspot.contents.preClickContent.title
                                        ? hotspot.contents.preClickContent.title
                                        : hotspotTypeEnumToString(hotspot.type),
                                    questionTime: hotspot.contents.preClickContent.question?.time!,
                                    answerHotspots: hotspot.groupedHotspots?.map((answerHotspot) => {
                                        return {
                                            hotspotId: answerHotspot.id,
                                            hotspotType: answerHotspot.type,
                                            hotspotTitle: answerHotspot.contents.preClickContent.title
                                                ? answerHotspot.contents.preClickContent.title
                                                : hotspotTypeEnumToString(answerHotspot.type),
                                            marked: true,
                                            weighting: getDefaultWeightingScore(answerHotspot),
                                        };
                                    }),
                                };
                                return questionHotspotObj;
                            } else {
                                const normalHotspotObj: MarkedHotspot = {
                                    hotspotId: hotspot.id,
                                    hotspotType: hotspot.type,
                                    hotspotTitle: hotspot.contents.preClickContent.title
                                        ? hotspot.contents.preClickContent.title
                                        : hotspotTypeEnumToString(hotspot.type),
                                    marked: false,
                                    weighting: 0,
                                };
                                return normalHotspotObj;
                            }
                        }),
                    };
                });

            if (marksData) {
                // prior marks data for this scenario exists
                const marksDataLocalSyncedWithServer = marshaledScenesDataAsMarks_!.map(
                    (localSceneMarks: ScenesMarks) => {
                        let sceneMarksDataFromServer = marksData!.find((serverSceneMarks: ScenesMarks) => {
                            return localSceneMarks.sceneId === serverSceneMarks.sceneId;
                        });

                        return { ...localSceneMarks, ...sceneMarksDataFromServer };
                    },
                );
                console.log("sync client with server", marksDataLocalSyncedWithServer);
                setMarshaledScenesDataAsMarks(marksDataLocalSyncedWithServer);
            } else {
                // no prior marks data for this scenario existed
                setMarshaledScenesDataAsMarks(marshaledScenesDataAsMarks_);
                updateMarks({
                    scenarioId,
                    data: marshaledScenesDataAsMarks,
                });
                console.log("marks data to send/post to api", marshaledScenesDataAsMarks_);
            }
        }
    }, [scenarioOverviewData, isLoadingMarks]);

    const [showSaveResult, setShowSaveResult] = useState<boolean>(false);

    // LocaleTimeString
    const [lastSuccessfulSave, setLastSuccessfulSave] = useState<string>();

    useEffect(() => {
        setLastSuccessfulSave(undefined);
        if (postingMarksIsSuccess) {
            setShowSaveResult(true);
            setLastSuccessfulSave(new Date().toLocaleTimeString());

            let tid = window.setTimeout(() => {
                setShowSaveResult(false);
            }, 2000);
        }
    }, [postingMarksIsSuccess]);

    useEffect(() => {
        setLastSuccessfulSave(undefined);
        if (postingMarksIsError) {
            setShowSaveResult(true);

            let tid = window.setTimeout(() => {
                setShowSaveResult(false);
            }, 2000);
        }
    }, [postingMarksIsError]);

    return (
        <Grid
            container
            item
            xs={12}
            sx={{
                position: "relative",
                display: "flex",
                justifyContent: "center",
            }}
        >
            <Grid
                item
                xs={6}
                sx={{
                    height: "calc(100vh - " + 68 + "px)",
                    paddingBottom: currentScenesTimelineHeight + 45 + "px",
                    overflowY: "scroll",
                    ...(!marshaledScenesDataAsMarks && {
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        flexDirection: "column",
                    }),
                    borderRight: "2px solid #888",
                    position: "relative",
                    marginTop: "45px",
                }}
            >
            
                <Box
                    component="div"
                    sx={{
                        position: "fixed",
                        top: "68px",
                        zIndex: 10000,
                        height: "45px",
                        // opacity: "0.5",
                        display: "flex",
                        alignItems: "center",
                        // flexDirection: "row-reverse",
                        justifyContent: "space-between",
                        width: "calc(50% - 40px)",
                        background: "#333",
                    }}
                >
                    <VTZoomInOut
                        sx={{ marginLeft: "5px" }}
                        zoomVal={tableZoom}
                        onZoomIn={() => {
                            if (tableZoom > 1) return;
                            setTableZoom(tableZoom + 0.05);
                        }}
                        onZoomOut={() => {
                            if (tableZoom < 0.45) return;
                            setTableZoom(tableZoom - 0.05);
                        }}
                    />

                    <Box
                        component="div"
                        sx={{
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        {isUpdatingMarks && (
                            <CircularProgress
                                size={30}
                                thickness={4}
                                sx={{
                                    marginRight: "5px",
                                    "& .MuiCircularProgress-circle": {
                                        color: theme.palette.primary.light,
                                    },
                                }}
                            />
                        )}
                        {showSaveResult && postingMarksIsSuccess && (
                            <DoneOutlineRoundedIcon sx={{ color: "#00cc66", marginRight: "10px", opacity: "1" }} />
                        )}

                        {showSaveResult && postingMarksIsError && (
                            <ErrorOutlineRoundedIcon sx={{ color: "red", fontSize: "2em", marginRight: "5px" }} />
                        )}

                        {lastSuccessfulSave &&
                            !postingMarksIsError &&
                            !showSaveResult &&
                            !isUpdatingMarks &&
                            postingMarksIsSuccess && (
                                <Typography sx={{ color: "#aaa", marginRight: "10px" }} variant="caption">
                                    {`last save ${lastSuccessfulSave}`}
                                </Typography>
                            )}

                        {/* <VTButton
                            sx={{ marginRight: "5px" }}
                            onClick={() => {
                                updateMarks({
                                    scenarioId,
                                    data: marshaledScenesDataAsMarks,
                                });
                            }}
                        >
                            save marks
                        </VTButton> */}
                        <Tooltip title={"save marks"}>
                            <IconButton
                                // disable={true}
                                sx={{ marginRight: "5px", borderRadius: "5px", height: "34px", width: "34px" }}
                                onClick={() => {
                                    updateMarks({
                                        scenarioId,
                                        data: marshaledScenesDataAsMarks,
                                    });
                                }}
                            >
                                <SaveRoundedIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
                {marshaledScenesDataAsMarks &&
                    marshaledScenesDataAsMarks.map((sceneWithMarks: ScenesMarks, sceneIndex: number) => {
                        let markedHotspotsInTableFormat = sceneWithMarks.sceneHotspots
                            .filter((hotspot) => {
                                return !("questionTime" in hotspot);
                            })
                            .map((markedHotspot) => {
                                markedHotspot = markedHotspot as MarkedHotspot;
                                return {
                                    id: markedHotspot.hotspotId,
                                    hotspotName: markedHotspot.hotspotTitle,
                                    marked: markedHotspot.marked,
                                    correct: markedHotspot.weighting > 0,
                                    weighting: markedHotspot.weighting,
                                };
                            });

                        let questionHotspots = sceneWithMarks.sceneHotspots.filter((hotspot) => {
                            return "questionTime" in hotspot;
                        });

                        console.log("question hotspots", questionHotspots);

                        return (
                            <VTMarksTableAccordianWrapper
                                sx={{ zoom: tableZoom, background: "#444" }}
                                title={`scene ${sceneIndex + 1}: ` + sceneWithMarks.sceneName}
                            >
                                {markedHotspotsInTableFormat.length > 0 && (
                                    <VTMarksDataEntryTable
                                        onHotspotNameClicked={(hotspotName: string, hotspotId: string) => {
                                            console.log("name, id", hotspotName, hotspotId);
                                        }}
                                        tableData={markedHotspotsInTableFormat}
                                        onDataChanged={function (newlyChangedData: VTMarksDataEntryRowObject[]): void {
                                            // throw new Error("Function not implemented.");

                                            const updatedMarksForScenario = marshaledScenesDataAsMarks!.map(
                                                (sceneMarks: ScenesMarks) => {
                                                    if (sceneMarks.sceneId === sceneWithMarks.sceneId) {
                                                        let updatedHotspotsForScene = sceneMarks.sceneHotspots.map(
                                                            (markedOrQuestionHotspot) => {
                                                                const foundChangedDataRow = newlyChangedData.find(
                                                                    (marksRowObject) => {
                                                                        return (
                                                                            marksRowObject.id ===
                                                                            markedOrQuestionHotspot.hotspotId
                                                                        );
                                                                    },
                                                                );
                                                                if (foundChangedDataRow) {
                                                                    return {
                                                                        ...markedOrQuestionHotspot,
                                                                        marked: foundChangedDataRow.marked,
                                                                        weighting: foundChangedDataRow.weighting,
                                                                    };
                                                                } else {
                                                                    return markedOrQuestionHotspot;
                                                                }
                                                            },
                                                        );

                                                        return {
                                                            ...sceneMarks,
                                                            sceneHotspots: updatedHotspotsForScene,
                                                        };
                                                    } else {
                                                        return sceneMarks;
                                                    }
                                                },
                                            );

                                            console.log("updated marks for scenario", updatedMarksForScenario);

                                            // do i have to set marshalledSceneDataAsMarks?
                                            setMarshaledScenesDataAsMarks(updatedMarksForScenario);

                                            // updateMarksDebounce(updatedMarksForScenario, 500);
                                        }}
                                    />
                                )}

                                {questionHotspots
                                    .sort((questionHotspotA, questionHotspotB) => {
                                        questionHotspotA = questionHotspotA as QuestionHotspot;
                                        questionHotspotB = questionHotspotB as QuestionHotspot;
                                        return questionHotspotA.questionTime - questionHotspotB.questionTime;
                                    }) // sort questions by times in order of earlier questions first
                                    .map((questionHotspot, questionIndex: number) => {
                                        questionHotspot = questionHotspot as QuestionHotspot;

                                        console.log("qh", questionHotspot.hotspotTitle, questionHotspot.answerHotspots);

                                        let answerHotspotsInTableFormat = questionHotspot.answerHotspots?.map(
                                            (markedHotspot) => {
                                                markedHotspot = markedHotspot as MarkedHotspot;
                                                return {
                                                    id: markedHotspot.hotspotId,
                                                    hotspotName: markedHotspot.hotspotTitle,
                                                    marked: markedHotspot.marked,
                                                    correct: markedHotspot.weighting > 0,
                                                    weighting: markedHotspot.weighting,
                                                };
                                            },
                                        );
                                        return (
                                            <VTMarksTableAccordianWrapper
                                                sx={{ background: "#444", marginTop: "15px" }}
                                                title={
                                                    questionHotspot.hotspotTitle + ` (Stop Point ${questionIndex + 1})`
                                                }
                                                questionTimeInMilliseconds={questionHotspot.questionTime * 1000}
                                            >
                                                <VTMarksDataEntryTable
                                                    tableData={answerHotspotsInTableFormat!}
                                                    onDataChanged={function (
                                                        newlyChangedData: VTMarksDataEntryRowObject[],
                                                    ): void {
                                                        // throw new Error("Function not implemented.");

                                                        console.log("newly changed data", newlyChangedData);

                                                        const updatedMarksForScenario = marshaledScenesDataAsMarks!.map(
                                                            (sceneMarks: ScenesMarks) => {
                                                                if (sceneMarks.sceneId === sceneWithMarks.sceneId) {
                                                                    return {
                                                                        ...sceneMarks,
                                                                        sceneHotspots: sceneMarks.sceneHotspots.map(
                                                                            (question) => {
                                                                                if (
                                                                                    question.hotspotId ===
                                                                                    questionHotspot.hotspotId
                                                                                ) {
                                                                                    return {
                                                                                        ...question,
                                                                                        answerHotspots: (
                                                                                            question as QuestionHotspot
                                                                                        ).answerHotspots?.map(
                                                                                            (answerHotspot) => {
                                                                                                const foundChangedDataRow =
                                                                                                    newlyChangedData.find(
                                                                                                        (
                                                                                                            marksRowObject,
                                                                                                        ) => {
                                                                                                            return (
                                                                                                                marksRowObject.id ===
                                                                                                                answerHotspot.hotspotId
                                                                                                            );
                                                                                                        },
                                                                                                    );
                                                                                                return {
                                                                                                    ...answerHotspot,
                                                                                                    weighting:
                                                                                                        foundChangedDataRow!
                                                                                                            .weighting,
                                                                                                    marked: foundChangedDataRow!
                                                                                                        .marked,
                                                                                                };
                                                                                            },
                                                                                        ),
                                                                                    };
                                                                                } else {
                                                                                    return question;
                                                                                }
                                                                            },
                                                                        ),
                                                                    };
                                                                } else {
                                                                    return sceneMarks;
                                                                }
                                                            },
                                                        );

                                                        console.log("updated answer marks", updatedMarksForScenario);

                                                        setMarshaledScenesDataAsMarks(updatedMarksForScenario);

                                                        // do i have to set marshalledSceneDataAsMarks?
                                                        // setMarshaledScenesDataAsMarks(updatedMarksForScenario);
                                                    }}
                                                />
                                            </VTMarksTableAccordianWrapper>
                                        );
                                    })}
                            </VTMarksTableAccordianWrapper>
                        );
                    })}
                {/* {marksMap ? (
                    isObjectEmpty(marksMap) ? (
                        <Typography variant="h4"> No Hotspots For This Scenario </Typography>
                    ) : (
                        Object.keys(marksMap).map((sceneName: string) => {
                            return (
                                <VTMarksTableAccordianWrapper sx={{ background: "#444" }} title={sceneName}>
                                    {marksMap[sceneName]["hotspots"] &&
                                        (marksMap[sceneName]["hotspots"] as HotspotMarks[]).length > 0 && (
                                            <VTMarksDataEntryTable
                                                onHotspotNameClicked={(parentHotspotName: string) => {
                                                    console.log(
                                                        "parent hotspot id = ",
                                                        hotspotNameIdMap.current[sceneName + "-" + parentHotspotName],
                                                    );
                                                }}
                                                tableData={marksMap[sceneName]["hotspots"] as HotspotMarks[]}
                                                onDataChanged={function (
                                                    newlyChangedData: VTMarksDataEntryRowObject[],
                                                ): void {
                                                    let marksMapCopy = createDeepCopy(marksMap) as MarksHotspotData;
                                                    (marksMap[sceneName]["hotspots"] as HotspotMarks[]) =
                                                        newlyChangedData;

                                                    // if not equal
                                                    // if (!_.isEqual(marksMap, marksMapCopy)) {
                                                    // console.log("should updateMarks");

                                                    // post request of marksCopy
                                                    updateMarks({
                                                        scenarioId: scenarioId,
                                                        data: marksMapCopy,
                                                    });
                                                    // }
                                                    // console.log("marksMapCopy changed", marksMapCopy);
                                                }}
                                            />
                                        )}{" "}
                                    {Object.keys(marksMap[sceneName])
                                        .filter((keyName: string) => {
                                            return keyName !== "hotspots";
                                        })
                                        .sort(
                                            (questionNameA, questionNameB) =>
                                                (marksMap[sceneName][questionNameA] as QuestionAnswerHotspots)
                                                    .timeInSeconds -
                                                (marksMap[sceneName][questionNameB] as QuestionAnswerHotspots)
                                                    .timeInSeconds,
                                        )
                                        .map((questionName: string) => {
                                            return (
                                                <VTMarksTableAccordianWrapper
                                                    sx={{ marginTop: "15px", marginLeft: "25px", background: "#444" }}
                                                    questionTimeInMilliseconds={
                                                        (marksMap[sceneName][questionName] as QuestionAnswerHotspots)
                                                            .timeInSeconds * 1000
                                                    }
                                                    title={questionName}
                                                >
                                                    <VTMarksDataEntryTable
                                                        onHotspotNameClicked={(childHotspotName: string) => {
                                                            // console.log(
                                                            //     "parent hotspot id = ",
                                                            //     hotspotNameIdMap.current[
                                                            //         sceneName +
                                                            //             "-" +
                                                            //             questionName +
                                                            //             "-" +
                                                            //             childHotspotName
                                                            //     ],
                                                            // );
                                                        }}
                                                        tableData={
                                                            (
                                                                marksMap[sceneName][
                                                                    questionName
                                                                ] as QuestionAnswerHotspots
                                                            ).hotspotMarks
                                                        }
                                                        onDataChanged={function (
                                                            newlyChangedData: VTMarksDataEntryRowObject[],
                                                        ): void {
                                                            let marksMapCopy = createDeepCopy(
                                                                marksMap,
                                                            ) as MarksHotspotData;
                                                            (
                                                                marksMapCopy[sceneName][
                                                                    questionName
                                                                ] as QuestionAnswerHotspots
                                                            ).hotspotMarks = newlyChangedData;

                                                            // if not equal
                                                            // if (!_.isEqual(marksMap, marksMapCopy)) {
                                                            // console.log("should updateMarks");
                                                            // post request of marksCopy
                                                            updateMarks({
                                                                scenarioId: scenarioId,
                                                                data: marksMapCopy,
                                                            });
                                                            // }
                                                            // console.log("marksMapCopy changed", marksMapCopy);
                                                        }}
                                                    />
                                                </VTMarksTableAccordianWrapper>
                                            );
                                        })}
                                </VTMarksTableAccordianWrapper>
                            );
                        })
                    )
                ) : (
                    <CircularProgress
                        size={100}
                        thickness={4}
                        sx={{
                            "& .MuiCircularProgress-circle": {
                                color: theme.palette.primary.light,
                            },
                        }}
                    />
                )} */}
                {(loading || isUpdatingMarks) && (
                    <>
                        <CircularProgress
                            size={100}
                            thickness={4}
                            sx={{
                                "& .MuiCircularProgress-circle": {
                                    color: theme.palette.primary.light,
                                },
                            }}
                        />
                        <Typography sx={{ marginTop: "20px" }} variant="body2">
                            {" "}
                            getting marks for this scenario
                        </Typography>
                    </>
                )}
                {/* {postingMarksIsSuccess && !loading && (
                    <>
                        <Typography
                            sx={{ marginleft: "20px", marginRight: "20px", textAlign: "center" }}
                            variant="body2"
                        >
                            {" "}
                            default marks data for this <br /> scenario successfully sent to server
                        </Typography>
                        <DoneOutlineRoundedIcon sx={{ color: "lime", marginTop: "15px" }} />
                    </>
                )} */}
                {/* {(postingMarksIsError || postingMarksIsUninitialized) && !loading && (
                    <>
                        <ErrorOutlineRoundedIcon sx={{ color: "red", fontSize: "4em" }} />
                        <Typography
                            sx={{ marginleft: "20px", marginRight: "20px", textAlign: "center", marginTop: "15px" }}
                            variant="body2"
                        >
                            {" "}
                            something went wrong sorry, try refreshing page
                        </Typography>
                    </>
                )} */}
            </Grid>
            {/* <Grid
                item
                xs={6}
                sx={{
                    height: "calc(100vh - " + 68 + "px)",
                    paddingBottom: currentScenesTimelineHeight + "px",
                    // width: "calc(100% - " + (currentSidePanel.width + 100) + "px)",
                    // marginTop: "68px",
                    // userSelect: "none",
                    // position: "relative",
                    // transition: "0.1s",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    // backgroundColor: "#a9a9a9",
                    overflowY: "auto",
                }}
            >
                {" "}
                <Typography variant="h4" sx={{ textAlign: "center" }}>
                    Hotspot Preview <br /> coming soon
                </Typography>{" "}
            </Grid> */}
        </Grid>
    );
}
